import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RocketIcon } from "lucide-react";
import { BoostProfileModal } from "./BoostProfileModal";

interface PortfolioVisitsChartProps {
  profileId: string;
}

export const PortfolioVisitsChart = ({ profileId }: PortfolioVisitsChartProps) => {
  const [isBoostModalOpen, setIsBoostModalOpen] = useState(false);
  const [userRole, setUserRole] = useState<"talent" | "recruiter">("talent");
  const queryClient = useQueryClient();

  // Fetch user role when component mounts
  useEffect(() => {
    const fetchUserRole = async () => {
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', profileId)
        .single();
      
      if (roleData) {
        setUserRole(roleData.role === 'recruiter' ? 'recruiter' : 'talent');
      }
    };

    fetchUserRole();
  }, [profileId]);

  const { mutate: recordVisit } = useMutation({
    mutationFn: async () => {
      const today = new Date().toISOString().split('T')[0];
      
      // First try to update existing record for today
      const { data: existingVisit, error: selectError } = await supabase
        .from("portfolio_visits")
        .select("id, visit_count")
        .eq("profile_id", profileId)
        .eq("visit_date", today)
        .single();

      if (selectError && selectError.code !== 'PGRST116') {
        console.error("Error checking existing visit:", selectError);
        throw selectError;
      }

      if (existingVisit) {
        // Update existing visit count
        const { error: updateError } = await supabase
          .from("portfolio_visits")
          .update({ visit_count: existingVisit.visit_count + 1 })
          .eq("id", existingVisit.id);

        if (updateError) {
          console.error("Error updating visit count:", updateError);
          throw updateError;
        }
      } else {
        // Create new visit record
        const { error: insertError } = await supabase
          .from("portfolio_visits")
          .insert([{ profile_id: profileId, visit_date: today, visit_count: 1 }]);

        if (insertError) {
          console.error("Error recording new visit:", insertError);
          throw insertError;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portfolio-visits", profileId] });
    },
  });

  // Fetch visits data
  const { data: visits, isLoading } = useQuery({
    queryKey: ["portfolio-visits", profileId],
    queryFn: async () => {
      console.log("Fetching portfolio visits for profile:", profileId);
      const { data, error } = await supabase
        .from("portfolio_visits")
        .select("visit_date, visit_count")
        .eq("profile_id", profileId)
        .order("visit_date", { ascending: true });

      if (error) {
        console.error("Error fetching portfolio visits:", error);
        throw error;
      }

      console.log("Portfolio visits data:", data);
      return data;
    },
  });

  // Record visit when component mounts (only if viewing someone else's profile)
  useEffect(() => {
    const checkUserAndRecordVisit = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user && user.id !== profileId) {
        recordVisit();
      }
    };

    checkUserAndRecordVisit();
  }, [profileId, recordVisit]);

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{userRole === 'recruiter' ? 'Opportunities Reach' : 'Portfolio Visits'}</CardTitle>
        </CardHeader>
        <CardContent className="h-[200px] flex items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </CardContent>
      </Card>
    );
  }

  // Generate dummy data that increases by day
  const today = new Date();
  const dummyData = Array.from({ length: 7 }, (_, i) => ({
    date: format(new Date(today.getTime() - (6 - i) * 24 * 60 * 60 * 1000), "MMM dd"),
    visits: Math.floor(100 + i * 15 + Math.random() * 20),
  }));

  // Calculate total views
  const totalViews = dummyData.reduce((sum, day) => sum + day.visits, 0);

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>{userRole === 'recruiter' ? 'Opportunities Reach' : 'Portfolio Visits'}</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">Total: {totalViews}</p>
        </div>
        <Button 
          className="bg-[#9b87f5] hover:bg-[#7E69AB] text-white"
          onClick={() => setIsBoostModalOpen(true)}
        >
          <RocketIcon className="mr-2 h-4 w-4" />
          Boost My Portfolio
        </Button>
      </CardHeader>
      <CardContent>
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dummyData}>
              <defs>
                <linearGradient id="visitGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis 
                dataKey="date" 
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tickLine={false}
              />
              <YAxis 
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="visits"
                stroke="hsl(var(--primary))"
                fillOpacity={1}
                fill="url(#visitGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>

      <BoostProfileModal 
        isOpen={isBoostModalOpen}
        onClose={() => setIsBoostModalOpen(false)}
        userRole={userRole}
      />
    </Card>
  );
};